import queryString from "query-string";
import React, { useMemo } from "react";
import {
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { ERoutes } from "../AppRouter";
import { EditState } from "../Types";

// Hook
export function useRouter() {
  const params = useParams();
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();

  // Return our custom router object
  // Memoize so that a new object is only returned if something changes
  const routerDetails = useMemo(() => {
    return {
      // For convenience add push(), replace(), pathname at top level
      push: history.push,
      replace: history.replace,
      pathname: location.pathname,
      // Merge params and parsed query string into single "query" object
      // so that they can be used interchangeably.
      // Example: /:topic?sort=popular -> { topic: "react", sort: "popular" }
      query: {
        ...queryString.parse(location.search), // Convert string to object
        ...params,
      } as any,
      // Include match, location, history objects so we have
      // access to extra React Router functionality if needed.
      match,
      location,
      history,
    };
  }, [params, match, location, history]);

  const editRouteParams = React.useMemo(() => {
    return {
      id: params["id"] as string,
      tab: params["tab"] as string,
      editState: params["state"] as EditState,
    };
  }, [params]);

  const goToEditPage = React.useCallback(
    (
      route: ERoutes,
      id: number,
      tab: string,
      editState: EditState = "edit"
    ) => {
      history.push(`${route}/${id}/${tab}/${editState}`);
    },
    [history]
  );

  const replaceEditRouteId = React.useCallback(
    (newId: number) => {
      const { tab, editState, id } = editRouteParams;
      if (newId === +id) return;

      const split = match.path.split("/:");
      const route = split[0];

      let newRoute = [route, newId, tab, editState].join("/");

      routerDetails.replace(newRoute);
    },
    [editRouteParams, match.path, routerDetails]
  );

  return {
    ...routerDetails,
    editRouteParams,
    goToEditPage,
    replaceEditRouteId,
  };
}
